import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { default as getObjectProperty } from 'lodash.get';
import { graphql, compose, withApollo } from 'react-apollo';
import { FormattedMessage } from 'react-intl';
import { Queries, getApiError } from '../api';
import { withAuth, authProptypes } from '../auth';
import { withAnalytics } from '../analytics';
import { toReadableStr } from '../utils/formatters';
import {
  setSteps,
  setFieldsFiltered,
  getData,
  getUpdated,
  PRICING
} from './pricingAggregator';

/**
 *
 * @function withPricing
 * @param {node} WrappedComponent
 * @returns {Component}
 */
const withPricing = WrappedComponent => {
  class PricingComponent extends Component {
    constructor(props) {
      super(props);

      this.state = {
        data: [], // for now, until BE query endpoint
        focus: 0,
        filtered: [],
        fields: [],
        selected: null,
        error: null,
        loading: false
      };
    }

    static propTypes = {
      analytics: PropTypes.object, // might not be loaded yet
      client: PropTypes.object,
      brands: PropTypes.arrayOf(PropTypes.object),
      brandsError: PropTypes.string,
      authUser: authProptypes.user
    };

    componentDidUpdate() {
      const { brands } = this.props; // from `Queries.getDivisions` query
      const { fields } = this.state;

      // when first field is not assigned yet, set it with brands
      if (!fields[0] && brands) {
        const brandField = {
          idx: 0,
          name: 'brand',
          label: 'Brand',
          options: brands
            .filter(brand => brand.pricingQuery)
            .map(brand => ({
              text: brand.name,
              value: brand.name
            }))
        };

        this.setInitialState(brandField);
      }
    }

    /**
     * @function setInitialState
     * @param {Object} brandField? (if not passed along, use the first field)
     * called from initial `componentDidUpdate` and on click on `Reset`
     */
    setInitialState = brandField => {
      const fields = [brandField || this.state.fields[0]];
      const sorted = setFieldsFiltered({
        filtered: [],
        fields,
        error: null
      });
      this.setState(sorted);
    };

    // values (optional) used only for force selecting one option
    applyResults = (changed, values) => {
      const { analytics } = this.props;
      const { idx, newFields, focusedData, current } = getUpdated({
        ...changed,
        ...this.state
      });

      let selected = null;
      let callback = () => {};
      let sorted = setFieldsFiltered({
        idx: idx + 1,
        focus: idx,
        filtered: focusedData,
        fields: newFields
      });
      const nextfield = sorted.fields[sorted.fields.length - 1];

      // stop if the next field or current field is price (which shouldn't be, but could happen because of data)
      if (
        focusedData.length === 1 &&
        [nextfield.name, changed.name].includes(PRICING)
      ) {
        selected = focusedData[0];
        // analytics part - format the selected object to `Attribute 1 | attr1, Attribute 2 | attr2` format
        const params = Object.keys(selected)
          .filter(
            key =>
              ![
                'price',
                'net_price',
                'currency',
                'updated_at',
                '__typename'
              ].includes(key) && !['', 'None', '>'].includes(selected[key])
          )
          .map(key => {
            return `${toReadableStr(key)} | ${selected[key]}`;
          });

        analytics.trackEvent('SRP generated', {
          'Query Parameters': params.join(', '),
          'Dollar Amount': selected[PRICING]
        });

        sorted = setFieldsFiltered({
          idx: idx,
          focus: idx,
          filtered: focusedData,
          fields: newFields
        });

        // when next field has only one option, force selecting it
        // formik doesn't do immediate update to `values`, so do it internally by passing `updated`
      } else if (nextfield.options && nextfield.options.length === 1) {
        const { value } = nextfield.options[0];
        // current is the key when this is backwards, then apply next field value
        const updated = changed.changeValue({
          ...values,
          ...current,
          [nextfield.name]: value
        });
        callback = () =>
          this.applyResults({ ...changed, ...nextfield, value }, updated);
      }

      this.setState({ ...sorted, selected }, callback);
    };

    getBrandCode = value => {
      const { brands } = this.props;
      const match = brands.filter(brand => brand.name === value);
      return match && match[0] && match[0].pricingQuery;
    };

    handleOnChange = async changed => {
      // when brand changed
      if (changed.idx === 0) {
        const brandCode = this.getBrandCode(changed.value);
        // if brand changed to empty value, set to initial state
        if (!brandCode) {
          this.setInitialState();
          return;
        }
        this.setState({ loading: true, error: null });

        const res = await this.fetchData(brandCode);
        const pricing =
          res && getObjectProperty(res, `data.${brandCode}`, undefined);
        if (pricing) {
          const data = getData(pricing);
          setSteps(data);
          this.setState({
            data,
            filtered: data,
            loading: false
          });
          this.applyResults(changed);
        } else {
          let error = (
            <FormattedMessage
              id="error.generic"
              defaultMessage="Something went wrong."
            />
          );
          if (res && res.errors && res.errors[0]) {
            error = res.errors[0].message;
          }
          this.setState({ error, loading: false });
        }

        // when other fields changed
      } else {
        this.applyResults(changed);
      }
    };

    // use `withApollo` injected client to use selected brand value
    fetchData = async brand => {
      const { client } = this.props;
      return await client.query({
        query: Queries.getPricing(brand)
      });
    };

    render() {
      return (
        <WrappedComponent
          {...this.props}
          fields={this.state.fields}
          selected={this.state.selected}
          setInitialState={this.setInitialState}
          handleOnChange={this.handleOnChange}
          error={this.state.error || this.props.brandsError}
          loading={this.state.loading}
        />
      );
    }
  }

  return compose(
    graphql(Queries.getDivisions, {
      options: () => ({
        fetchPolicy: 'network-only'
      }),
      props: props => ({
        ...props,
        brands: getObjectProperty(props, 'data.getDivisions', undefined),
        brandsError: getApiError(props)
      })
    }),
    withApollo,
    withAuth,
    withAnalytics
  )(PricingComponent);
};

export default withPricing;



// WEBPACK FOOTER //
// ./src/hoc/withPricing.js